import styled, { createGlobalStyle } from "styled-components";

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  body {
    font-family: ${({ theme }) => theme.typography.fontFamily.primary};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    color: ${({ theme }) => theme.colors.text.primary};
    background-color: ${({ theme }) => theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    min-height: 100vh;
    margin: 0;
    padding: 0;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
    background: none;
    transition: ${({ theme }) => theme.transitions.fast};
  }

  input, textarea, select {
    font-family: inherit;
    outline: none;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  ul, ol {
    list-style: none;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  }

  h1 { font-size: ${({ theme }) => theme.typography.fontSize["3xl"]}; }
  h2 { font-size: ${({ theme }) => theme.typography.fontSize["2xl"]}; }
  h3 { font-size: ${({ theme }) => theme.typography.fontSize.xl}; }
  h4 { font-size: ${({ theme }) => theme.typography.fontSize.lg}; }
  h5 { font-size: ${({ theme }) => theme.typography.fontSize.base}; }
  h6 { font-size: ${({ theme }) => theme.typography.fontSize.sm}; }
`;

// Styled components reutilizáveis
export const Container = styled.div<{ maxWidth?: string }>`
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || "1200px"};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0 ${({ theme }) => theme.spacing.lg};
  }
`;

export const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => !["padding", "noBorder"].includes(prop),
})<{ padding?: string; noBorder?: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid
    ${({ theme, noBorder }) =>
      noBorder ? "transparent" : theme.colors.border.light};
  padding: ${({ padding, theme }) => padding || theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

export const FlexContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["direction", "align", "justify", "gap", "wrap"].includes(prop),
})<{
  direction?: "row" | "column";
  align?: "flex-start" | "center" | "flex-end" | "stretch";
  justify?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around";
  gap?: string;
  wrap?: boolean;
}>`
  display: flex;
  flex-direction: ${({ direction }) => direction || "row"};
  align-items: ${({ align }) => align || "center"};
  justify-content: ${({ justify }) => justify || "flex-start"};
  gap: ${({ gap, theme }) => gap || theme.spacing.md};
  flex-wrap: ${({ wrap }) => (wrap ? "wrap" : "nowrap")};
`;

export const Grid = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["columns", "gap", "minColumnWidth"].includes(prop),
})<{
  columns?: number;
  gap?: string;
  minColumnWidth?: string;
}>`
  display: grid;
  grid-template-columns: ${({ columns, minColumnWidth }) =>
    columns
      ? `repeat(${columns}, 1fr)`
      : `repeat(auto-fit, minmax(${minColumnWidth || "250px"}, 1fr))`};
  gap: ${({ gap, theme }) => gap || theme.spacing.lg};
`;

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !["variant", "size"].includes(prop),
})<{
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ variant = "primary", theme }) => {
    switch (variant) {
      case "primary":
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.text.white};
          &:hover { background-color: ${theme.colors.primaryDark}; }
        `;
      case "secondary":
        return `
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.text.white};
          &:hover { background-color: ${theme.colors.secondaryLight}; }
        `;
      case "outline":
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.primary};
          &:hover { background-color: ${theme.colors.primary}; color: ${theme.colors.text.white}; }
        `;
      case "ghost":
        return `
          background-color: transparent;
          color: ${theme.colors.text.secondary};
          &:hover { background-color: ${theme.colors.surfaceLight}; }
        `;
      default:
        return "";
    }
  }}

  ${({ size = "md", theme }) => {
    switch (size) {
      case "sm":
        return `
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          font-size: ${theme.typography.fontSize.sm};
        `;
      case "md":
        return `
          padding: ${theme.spacing.md} ${theme.spacing.lg};
          font-size: ${theme.typography.fontSize.base};
        `;
      case "lg":
        return `
          padding: ${theme.spacing.lg} ${theme.spacing.xl};
          font-size: ${theme.typography.fontSize.lg};
        `;
      default:
        return "";
    }
  }}
`;
